import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { passwordResetService } from '../services/passwordReset';

const ResetPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const token = searchParams.get('token');
    const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

    useEffect(() => {
        if (token) {
            const isValid = passwordResetService.validateToken(token);
            setIsValidToken(isValid);
        } else {
            setIsValidToken(false);
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');

        // Validation
        if (password !== confirmPassword) {
            setError(t('resetPassword.passwordsNotMatch'));
            setIsLoading(false);
            return;
        }

        if (password.length < 6) {
            setError(t('resetPassword.passwordTooShort'));
            setIsLoading(false);
            return;
        }

        if (!token) {
            setError(t('resetPassword.invalidToken'));
            setIsLoading(false);
            return;
        }

        try {
            const response = await passwordResetService.resetPassword(token, password);
            
            if (response.success) {
                setMessage(response.message);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(response.message);
            }
        } catch (error) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isValidToken === false) {
        return (
            <div className="auth-page">
                <div className="auth-container">
                    <motion.div
                        className="auth-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="auth-header">
                            <h1>{t('resetPassword.invalidLink')}</h1>
                            <p>{t('resetPassword.invalidLinkMessage')}</p>
                        </div>
                        <div className="auth-footer">
                            <Link to="/forgot-password" className="btn btn-primary">
                                {t('resetPassword.requestNewLink')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (isValidToken === null) {
        return (
            <div className="auth-page">
                <div className="auth-container">
                    <div className="auth-card">
                        <div className="auth-header">
                            <h1>Loading...</h1>
                            <p>Validating reset link...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                <motion.div
                    className="auth-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="auth-header">
                        <h1>{t('resetPassword.title')}</h1>
                        <p>{t('resetPassword.subtitle')}</p>

                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="password">{t('resetPassword.newPassword')}</label>
                            <div className="password-input">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={t('resetPassword.passwordPlaceholder')}
                                    required
                                    className="input"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">{t('resetPassword.confirmPassword')}</label>
                            <div className="password-input">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                                    required
                                    className="input"
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                        </div>

                        <div className="password-requirements">
                            <h4>{t('resetPassword.requirements')}</h4>
                            <ul>
                                <li className={password.length >= 6 ? 'valid' : ''}>
                                    {t('resetPassword.minLength')}
                                </li>
                                <li className={password !== confirmPassword && confirmPassword ? 'invalid' : password === confirmPassword && confirmPassword ? 'valid' : ''}>
                                    {t('resetPassword.passwordsMatch')}
                                </li>
                            </ul>
                        </div>

                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}

                        {message && (
                            <motion.div
                                className="success-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {message}
                            </motion.div>
                        )}

                        <motion.button
                            type="submit"
                            className="btn btn-primary auth-btn"
                            disabled={isLoading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {isLoading ? t('resetPassword.resetting') : t('resetPassword.resetPassword')}
                        </motion.button>
                    </form>

                    <div className="auth-footer">
                        <p>
                            {t('resetPassword.rememberPassword')}{' '}
                            <Link to="/login" className="auth-link">
                                {t('resetPassword.backToLogin')}
                            </Link>
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ResetPassword;
